import { OffsetMap } from '../types/offsetMap'
import { calculateCameraVisibility, isProjectionCentral } from './cameraProjection'
import { calculateCameraSpaceDepth } from './ThumbnailScaleCalculator'

/**
 * Camera ranking result for thumbnail display
 */
export interface RankedCamera {
  id: number
  frameIndex: number
  distance: number
  quality: number
  frameFile: string
  intrinsics: {
    width: number
    height: number
  }
  thumbnailUrl?: string
}

/**
 * Camera Ranking Utilities
 * 
 * Provides functions to rank cameras by their suitability for viewing a specific location.
 * Rankings consider 3D distance and projection quality (how centered the point appears).
 */

/**
 * Calculate 3D distance between two camera positions
 */
function calculate3DDistance(
  camera1: { position: [number, number, number] },
  camera2: { position: [number, number, number] }
): number {
  const [x1, y1, z1] = camera1.position
  const [x2, y2, z2] = camera2.position
  const dx = x2 - x1
  const dy = y2 - y1
  const dz = z2 - z1
  const dist = Math.sqrt(dx * dx + dy * dy + dz * dz)
  return dist
}

/**
 * Get ranked nearby cameras for a tap location
 * 
 * @param tapPoint - { x, y } coordinates in display pixels
 * @param containerRect - Bounding rectangle of clicked container
 * @param currentCamera - Camera data for current frame
 * @param allCameras - Array of all camera data
 * @param points3D - Array of 3D points
 * @param offsetMap - Connectivity information
 * @param currentFrameIndex - Index of current frame
 * @param maxResults - Maximum number of cameras to return (default: 100)
 * @returns Array of ranked cameras sorted by score (best first)
 */
export function getRankedNearbyCameras(
  tapPoint: { x: number; y: number },
  containerRect: DOMRect,
  currentCamera: any,
  allCameras: any[],
  points3D: any[],
  offsetMap: OffsetMap | null,
  currentFrameIndex: number,
  _maxResults: number = 100
): RankedCamera[] {
  // First get cameras that can see the tapped point
  const { highlightedCameras, projections } = calculateCameraVisibility(
    tapPoint.x,
    tapPoint.y,
    containerRect,
    currentCamera,
    allCameras,
    points3D,
    offsetMap,
    currentFrameIndex
  )
  
  // CRITICAL: Only use cameras that are in the projections map (have valid projections)
  const validCameras = Array.from(highlightedCameras).filter(frameIndex => 
    projections.has(frameIndex)
  )
  
  if (validCameras.length === 0) {
    return []
  }

  // Convert to array and calculate scores
  const cameraScores: Array<{
    frameIndex: number
    distance: number
    quality: number
    score: number
  }> = []

  for (const frameIndex of validCameras) {
    const camera = allCameras[frameIndex]
    if (!camera) {
      continue
    }

    // Calculate 3D distance from current camera
    const distance = calculate3DDistance(currentCamera, camera)
    
    // console.log(`   Frame ${frameIndex}: position [${camera.position[0].toFixed(2)}, ${camera.position[1].toFixed(2)}, ${camera.position[2].toFixed(2)}] distance=${distance.toFixed(2)}`)

    // For projection quality, we'd need to project the tapped 3D point to this camera
    // For now, use a simple heuristic based on connectivity
    // TODO: Implement proper projection quality calculation using calculateProjectionQuality
    const quality = frameIndex === currentFrameIndex ? 1.0 : 0.8

    // Combined score: prioritize closer cameras with better projection
    const score = (1 / (1 + distance)) * quality

    // console.log(`         quality=${quality} score=${score.toFixed(4)}`)

    cameraScores.push({
      frameIndex,
      distance,
      quality,
      score
    })
  }

  // Sort by score (highest first) and take top results
  cameraScores.sort((a, b) => b.score - a.score)
  
  const result = cameraScores.map(item => {
    const camera = allCameras[item.frameIndex]
    return {
      id: item.frameIndex,
      frameIndex: item.frameIndex,
      distance: item.distance,
      quality: item.quality,
      frameFile: camera?.frame_file || '',
      intrinsics: camera?.intrinsics || { width: 450, height: 800 },
      thumbnailUrl: undefined // TODO: Generate thumbnail URLs
    }
  })
  
  return result
}

/**
 * Get ranked nearby cameras using IMAGE PIXEL coordinates (zoom-independent)
 * 
 * CRITICAL: This function takes pre-calculated image pixel coordinates
 * that were stored at tap time and uses them directly for discovery.
 * This completely bypasses the container size dependency, making it
 * zoom-independent and reliable across zoom level changes.
 * 
 * @param imagePixelX - X coordinate in image space (0 to frameCamera.intrinsics.width)
 * @param imagePixelY - Y coordinate in image space (0 to frameCamera.intrinsics.height)
 * @param currentCamera - Camera data for current frame
 * @param allCameras - Array of all camera data
 * @param points3D - Array of 3D points
 * @param offsetMap - Connectivity information
 * @param currentFrameIndex - Index of current frame
 * @param maxResults - Maximum number of cameras to return (default: 100)
 * @returns Array of ranked cameras sorted by score (best first)
 */
export function getRankedNearbyCamerasByImagePixels(
  imagePixelX: number,
  imagePixelY: number,
  currentCamera: any,
  allCameras: any[],
  points3D: any[],
  offsetMap: OffsetMap | null,
  currentFrameIndex: number,
  _maxResults: number = 100,
  edgeMarginPercent: number = 0.05
): RankedCamera[] {
  // Convert image pixels to display coordinates for calculateCameraVisibility
  const fakeContainerRect = new DOMRect(0, 0, currentCamera.intrinsics.width, currentCamera.intrinsics.height)
  const normalizedDisplayX = imagePixelX
  const normalizedDisplayY = imagePixelY
  
  // Call calculateCameraVisibility with the image pixel coordinates
  // Using intrinsics as the container size ensures proper mapping
  const { highlightedCameras, projections, point3D } = calculateCameraVisibility(
    normalizedDisplayX,
    normalizedDisplayY,
    fakeContainerRect,
    currentCamera,
    allCameras,
    points3D,
    offsetMap,
    currentFrameIndex
  )
  
  // Only use cameras that are in the projections map (have valid projections)
  const validCameras = Array.from(highlightedCameras).filter(frameIndex => 
    projections.has(frameIndex)
  )
  
  // Filter out cameras where projection is too close to edges
  const camerasNotNearEdge = validCameras.filter(frameIndex => {
    const camera = allCameras[frameIndex]
    if (!camera) return false
    
    const projection = projections.get(frameIndex)
    if (!projection) return false
    
    const [projX, projY] = projection
    const imageWidth = camera.intrinsics.width
    const imageHeight = camera.intrinsics.height
    
    const marginX = imageWidth * edgeMarginPercent
    const marginY = imageHeight * edgeMarginPercent
    
    const isNotNearEdge = 
      projX >= marginX && 
      projX <= (imageWidth - marginX) &&
      projY >= marginY && 
      projY <= (imageHeight - marginY)
    
    if (!isNotNearEdge) {
      console.log(`[CameraRankingUtils] Filtered camera ${frameIndex}: projection [${projX.toFixed(1)}, ${projY.toFixed(1)}] too close to edge (margin: ${edgeMarginPercent * 100}%, ${marginX.toFixed(1)}px)`)
    }
    
    return isNotNearEdge
  })
  
  console.log(`[CameraRankingUtils] Edge filtering: ${validCameras.length} valid cameras → ${camerasNotNearEdge.length} not near edge (${edgeMarginPercent * 100}% margin)`)
  
  if (camerasNotNearEdge.length === 0) {
    console.log('[CameraRankingUtils] No cameras passed edge filter, returning empty array')
    return []
  }

  // Convert to array and calculate scores
  const cameraScores: Array<{
    frameIndex: number
    distance: number
    quality: number
    score: number
    isCentral: boolean
  }> = []

  for (const frameIndex of camerasNotNearEdge) {
    const camera = allCameras[frameIndex]
    if (!camera) {
      continue
    }

    // Calculate camera space depth (distance from point to camera plane)
    const depth = point3D ? calculateCameraSpaceDepth(point3D, camera) : calculate3DDistance(currentCamera, camera)

    // Check if projection is central (point near image center = better view)
    const projected = projections.get(frameIndex)
    const isCentral = projected ? isProjectionCentral(projected, camera.intrinsics, 0.4) : false
    const centralityBonus = isCentral ? 1.2 : 1

    const quality = frameIndex === currentFrameIndex ? 1.0 : 0.8

    // Combined score: centrality × (1 / depth) × quality
    const distanceFactor = 1 / (1 + depth)
    const score = centralityBonus * distanceFactor * quality

    cameraScores.push({
      frameIndex,
      distance: depth,
      quality,
      score,
      isCentral
    })
  }

  // ========================================
  // SIMPLIFIED RANKING: Depth-first, then pick best central from top N
  // ========================================
  
  // Step 1: Sort by depth (closest first)
  cameraScores.sort((a, b) => a.distance - b.distance)
  
  // Step 2: Find the depth window threshold (closest depth + 50%)
  const DEPTH_WINDOW_PERCENT = 0.5  // 50% of closest depth - cameras within this range are "equally close"
  const closestDepth = cameraScores[0]?.distance ?? 0
  const depthThreshold = closestDepth * (1 + DEPTH_WINDOW_PERCENT)
  
  // Step 3: Find all cameras within the depth window
  const camerasInWindow = cameraScores.filter(c => c.distance <= depthThreshold)
  
  // Step 4: Among cameras in window, find the best central one (if any)
  const centralInWindow = camerasInWindow.filter(c => c.isCentral)
  
  if (centralInWindow.length > 0) {
    // Sort central cameras by depth to get the closest central one
    centralInWindow.sort((a, b) => a.distance - b.distance)
    const bestCentral = centralInWindow[0]
    
    // Move best central to position #1
    const bestCentralIndex = cameraScores.findIndex(c => c.frameIndex === bestCentral.frameIndex)
    if (bestCentralIndex > 0) {
      const [removed] = cameraScores.splice(bestCentralIndex, 1)
      cameraScores.unshift(removed)
    }
  }
  
  const result = cameraScores.map(item => {
    const camera = allCameras[item.frameIndex]
    return {
      id: item.frameIndex,
      frameIndex: item.frameIndex,
      distance: item.distance,
      quality: item.quality,
      frameFile: camera?.frame_file || '',
      intrinsics: camera?.intrinsics || { width: 450, height: 800 },
      thumbnailUrl: undefined
    }
  })
  
  return result
}